import { useState } from 'react';
import { useLocation } from 'react-router';
import { KeyRound } from 'lucide-react';
import { Button, Field, InlineAlert, Modal, PasswordInput, PasswordStrength, passwordIssues, useToast } from '@/components/ui';
import { api, describeError, isApiError } from '@/lib/api';
import { useSession } from '@/lib/session';

const PUBLIC_PATHS = ['/login', '/signup', '/forgot-password', '/reset-password', '/invite', '/login/2fa'];

/**
 * Shown after sign-in when an administrator created the account with an initial password. The user
 * must choose their own before continuing; the server clears `mustChangePassword` on success.
 * Sign-out stays available so a shared device is never trapped.
 */
export function PasswordChangeGate() {
  const session = useSession();
  const { pathname } = useLocation();
  const toast = useToast();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<{ title: string; message: string; field?: string } | null>(null);
  if (!session.user?.mustChangePassword || session.impersonation || PUBLIC_PATHS.some((p) => pathname.startsWith(p))) return null;

  const issues = passwordIssues(next);
  const errors = {
    current: touched && !current ? 'Enter the password you signed in with.' : error?.field === 'currentPassword' ? error.message : null,
    next: touched && issues.length ? issues[0]! : error?.field === 'newPassword' ? error.message : null,
    confirm: touched && confirm !== next ? 'The passwords don’t match.' : null,
  };

  const submit = async () => {
    setTouched(true);
    setError(null);
    if (!current || issues.length || confirm !== next) return;
    setBusy(true);
    try {
      await api.post('/auth/password/change', { currentPassword: current, newPassword: next });
      await session.refresh();
      toast.show({ tone: 'success', title: 'Password updated', description: 'Use your new password next time you sign in.' });
    } catch (e) {
      const fe = isApiError(e) ? e.fieldErrors() : {};
      const field = Object.keys(fe)[0];
      setError(field ? { title: 'Check your details', message: fe[field]!, field } : { ...describeError(e) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      onOpenChange={() => undefined}
      dismissible={false}
      title="Choose your own password"
      description="Your administrator created this account with a temporary password. Choose a new one that only you know to continue."
      footer={
        <>
          <Button variant="ghost" onClick={() => void session.signOut().then(() => window.location.replace('/login'))} disabled={busy}>
            Sign out
          </Button>
          <Button variant="primary" icon={<KeyRound size={15} />} loading={busy} onClick={() => void submit()}>
            Save new password
          </Button>
        </>
      }
    >
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <Field label="Temporary password" required error={errors.current}>
          <PasswordInput value={current} onChange={setCurrent} autoComplete="current-password" autoFocus />
        </Field>
        <Field label="New password" required error={errors.next} help={!errors.next ? 'At least 12 characters. A short phrase is easier to remember.' : undefined}>
          <PasswordInput value={next} onChange={setNext} autoComplete="new-password" />
        </Field>
        <PasswordStrength value={next} />
        <Field label="Confirm new password" required error={errors.confirm}>
          <PasswordInput value={confirm} onChange={setConfirm} autoComplete="new-password" />
        </Field>
        {error && !error.field && (
          <InlineAlert tone="danger" title={error.title}>
            {error.message}
          </InlineAlert>
        )}
        <button type="submit" hidden />
      </form>
    </Modal>
  );
}
